/**
 * Subscribe form page context (source URL + context type/title).
 *
 * @package DemocraticTools
 */

/**
 * @typedef {Object} SubscribeContext
 * @property {string} source_url
 * @property {string} context_type
 * @property {string} context_title
 */

/**
 * Derive subscribe context from the current page URL and document title.
 *
 * @param {Pick<Location, 'href' | 'pathname' | 'hash'>} [locationLike]
 * @param {Pick<Document, 'title'>} [documentLike]
 * @returns {SubscribeContext}
 */
export function detectSubscribeContext(
  locationLike = globalThis.location,
  documentLike = globalThis.document,
) {
  const href = typeof locationLike?.href === 'string' ? locationLike.href : '';
  const pathname = typeof locationLike?.pathname === 'string' ? locationLike.pathname : '';
  const normalized = pathname.replace(/\/+$/, '') || '/';
  const contextTitle =
    typeof documentLike?.title === 'string' ? documentLike.title.trim() : '';

  let contextType = 'newsletter';

  if (normalized === '/articles' || normalized.startsWith('/articles/')) {
    contextType = 'articles';
  } else if (normalized === '/newsletter' || normalized.startsWith('/newsletter/')) {
    contextType = 'newsletter';
  } else if (normalized === '/') {
    contextType = 'home';
  }

  return {
    source_url: href,
    context_type: contextType,
    context_title: contextTitle,
  };
}

/**
 * Merge live page context into a subscribe REST payload.
 *
 * @param {Record<string, unknown>} payload
 * @param {Pick<Location, 'href' | 'pathname' | 'hash'>} [locationLike]
 * @param {Pick<Document, 'title'>} [documentLike]
 * @returns {Record<string, unknown>}
 */
export function withSubscribeContext(
  payload,
  locationLike = globalThis.location,
  documentLike = globalThis.document,
) {
  const context = detectSubscribeContext(locationLike, documentLike);

  return {
    ...payload,
    source_url: context.source_url,
    context_type: context.context_type,
    context_title: context.context_title,
  };
}

/**
 * @param {HTMLFormElement} form
 * @returns {Array<{ code: string, frequency: string, subscribed: boolean }> | null}
 */
export function readSubscribeProductSelections(form) {
  const host = form.querySelector('[data-dt-subscribe-products]');

  if (!host) {
    return null;
  }

  /** @type {Array<{ code: string, frequency: string, subscribed: boolean }>} */
  const items = [];

  host.querySelectorAll('input[type="checkbox"][data-dt-subscribe-product]').forEach((input) => {
    const code = String(input.getAttribute('data-dt-subscribe-product') ?? '').trim();

    if (code === '') {
      return;
    }

    items.push({
      code,
      frequency: String(input.getAttribute('data-dt-subscribe-product-frequency') ?? '').trim(),
      subscribed: input.checked === true || input.hasAttribute('checked'),
    });
  });

  return items;
}

/**
 * @param {HTMLFormElement} form
 * @param {Record<string, unknown>} payload
 * @returns {Record<string, unknown>}
 */
export function withSubscribeProductPayload(form, payload) {
  const selections = readSubscribeProductSelections(form);

  if (selections === null) {
    return payload;
  }

  return {
    ...payload,
    product_subscriptions: selections,
  };
}

const CONFIRMED_QUERY = 'subscription=confirmed';
const TOAST_MESSAGE = '\u2713 Subscription confirmed! You\u2019re all set.';
const TOAST_DISMISS_MS = 5000;

/**
 * Whether the landing page should show the post-confirm toast.
 *
 * @param {Pick<Location, 'search'>} [locationLike]
 * @returns {boolean}
 */
export function hasSubscriptionConfirmedQuery(locationLike = globalThis.location) {
  const search = typeof locationLike?.search === 'string' ? locationLike.search : '';

  if (!search.includes(CONFIRMED_QUERY)) {
    return false;
  }

  return new URLSearchParams(search).get('subscription') === 'confirmed';
}

/**
 * Remove the subscription=confirmed query flag without reloading.
 *
 * @param {Pick<Location, 'pathname' | 'search'>} [locationLike]
 * @param {Pick<Document, 'title'>} [documentLike]
 * @param {Pick<History, 'replaceState'>} [historyLike]
 */
export function cleanSubscriptionConfirmedUrl(
  locationLike = globalThis.location,
  documentLike = globalThis.document,
  historyLike = globalThis.history,
) {
  if (typeof historyLike?.replaceState !== 'function') {
    return;
  }

  const pathname = typeof locationLike?.pathname === 'string' ? locationLike.pathname : '/';
  const title = typeof documentLike?.title === 'string' ? documentLike.title : '';

  historyLike.replaceState({}, title, pathname);
}

/**
 * @param {Document} documentLike
 * @param {string} message
 * @returns {HTMLElement}
 */
export function createSubscriptionConfirmedToast(documentLike, message = TOAST_MESSAGE) {
  const toast = documentLike.createElement('div');
  toast.className = 'dt-subscription-toast';
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');

  const text = documentLike.createElement('p');
  text.className = 'dt-subscription-toast__message';
  text.textContent = message;

  const close = documentLike.createElement('button');
  close.type = 'button';
  close.className = 'dt-subscription-toast__close';
  close.setAttribute('aria-label', 'Dismiss notification');
  close.textContent = '\u2715';

  toast.append(text, close);

  return toast;
}

/**
 * Fade out and remove a toast node.
 *
 * @param {HTMLElement} toast
 * @param {number} [fadeMs]
 */
export function dismissSubscriptionToast(toast, fadeMs = 300, timers = globalThis) {
  if (!toast?.isConnected) {
    return;
  }

  toast.classList.add('dt-subscription-toast--dismissed');

  const scheduleDismiss = typeof timers.setTimeout === 'function'
    ? timers.setTimeout.bind(timers)
    : globalThis.setTimeout.bind(globalThis);

  scheduleDismiss(() => {
    toast.remove();
  }, fadeMs);
}

/**
 * Show a one-time toast when redirected after email confirmation.
 *
 * @param {Pick<Location, 'pathname' | 'search'>} [locationLike]
 * @param {Document} [documentLike]
 * @param {Pick<History, 'replaceState'>} [historyLike]
 * @param {{ setTimeout?: typeof globalThis.setTimeout, clearTimeout?: typeof globalThis.clearTimeout }} [timers]
 */
export function initSubscriptionConfirmedToast(
  locationLike = globalThis.location,
  documentLike = globalThis.document,
  historyLike = globalThis.history,
  timers = globalThis,
) {
  if (!hasSubscriptionConfirmedQuery(locationLike)) {
    return;
  }

  const body = documentLike?.body;

  if (!body) {
    return;
  }

  cleanSubscriptionConfirmedUrl(locationLike, documentLike, historyLike);

  const toast = createSubscriptionConfirmedToast(documentLike);
  body.append(toast);

  requestAnimationFrame(() => {
    toast.classList.add('dt-subscription-toast--visible');
  });

  const scheduleDismiss = typeof timers.setTimeout === 'function'
    ? timers.setTimeout.bind(timers)
    : globalThis.setTimeout.bind(globalThis);
  const cancelDismiss = typeof timers.clearTimeout === 'function'
    ? timers.clearTimeout.bind(timers)
    : globalThis.clearTimeout.bind(globalThis);

  let dismissTimer = scheduleDismiss(() => {
    dismissSubscriptionToast(toast, 300, timers);
  }, TOAST_DISMISS_MS);

  toast.querySelector('.dt-subscription-toast__close')?.addEventListener('click', () => {
    cancelDismiss(dismissTimer);
    dismissSubscriptionToast(toast, 300, timers);
  });
}
